import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Music, Clock, Camera, User, Trophy } from 'lucide-react';
import { useFutebolRealtime } from '@/features/futebol';

interface Props {
  telaId: string;
  onFoto: () => void;
  /** Teclado virtual aberto — a nav sai da tela para não roubar altura útil. */
  oculta?: boolean;
}

export default function JukeboxBottomNav({ telaId, onFoto, oculta = false }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { partida } = useFutebolRealtime(telaId);

  const basePath = `/jukebox/${telaId}`;
  const currentTab = location.pathname === basePath || location.pathname === `${basePath}/`
    ? 'home'
    : location.pathname.includes('/historico')
      ? 'historico'
      : location.pathname.includes('/jogo')
        ? 'jogo'
        : location.pathname.includes('/perfil')
          ? 'perfil'
          : 'home';

  const jogoAoVivo = !!partida?.fixture_id && partida.tempo_jogo !== 'FT';

  const goTo = useCallback((path: string) => {
    navigate(path);
  }, [navigate]);

  return (
    <nav
      className={`
        fixed bottom-0 left-0 right-0 z-30 border-t border-white/[0.05] bg-jukebox-bg/95
        pb-[env(safe-area-inset-bottom,0px)] backdrop-blur-xl
        transition-transform duration-200
        ${oculta ? 'pointer-events-none translate-y-full' : 'translate-y-0'}
      `}
      aria-hidden={oculta || undefined}
    >
      <div className="mx-auto grid h-16 max-w-md grid-cols-5 items-center px-1">
        <NavTab
          icon={Music}
          label="Jukebox"
          active={currentTab === 'home'}
          onClick={() => goTo(basePath)}
          compact
        />

        <NavTab
          icon={Clock}
          label="Histórico"
          active={currentTab === 'historico'}
          onClick={() => goTo(`${basePath}/historico`)}
          compact
        />

        <button
          type="button"
          onClick={onFoto}
          className="
            relative -mt-5 flex h-14 w-14 shrink-0 items-center justify-center justify-self-center rounded-full
            bg-gradient-to-br from-orange-500 to-rose-500 shadow-lg shadow-orange-500/30
            transition-transform active:scale-95
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60
          "
          aria-label="Foto na TV"
        >
          <Camera size={24} className="text-white" strokeWidth={2.2} />
        </button>

        <NavTab
          icon={Trophy}
          label="Jogo"
          active={currentTab === 'jogo'}
          onClick={() => goTo(`${basePath}/jogo`)}
          compact
          live={jogoAoVivo}
        />

        <NavTab
          icon={User}
          label="Perfil"
          active={currentTab === 'perfil'}
          onClick={() => goTo(`${basePath}/perfil`)}
          compact
        />
      </div>
    </nav>
  );
}

interface NavTabProps {
  icon: typeof Music;
  label: string;
  active: boolean;
  onClick: () => void;
  compact?: boolean;
  live?: boolean;
}

function NavTab({ icon: Icon, label, active, onClick, compact, live }: NavTabProps) {
  return (
    <button
      onClick={onClick}
      className={`relative flex min-h-11 flex-col items-center justify-center gap-0.5 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/60 rounded-lg transition-colors ${
        compact ? 'px-1.5 min-w-0 w-full' : 'px-3'
      } ${active ? 'text-orange-400' : 'text-white/50'}`}
    >
      <span className="relative">
        <Icon size={compact ? 20 : 22} strokeWidth={active ? 2.2 : 1.8} />
        {live && (
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-rose-500 animate-pulse ring-2 ring-jukebox-bg" />
        )}
      </span>
      <span className="max-w-full truncate text-xs font-medium">
        {label}
      </span>
    </button>
  );
}
